import { normalizeSingleFilterFormData, sanitizeFilters } from "@/helpers/request-helpers";
import ICoverageArea from "@/interfaces/coverage-area.interface";
import { FilterType, FilterValue, IFilter } from "@/types/filter.types";

import { CoverageAreaFilter, CoverageAreaFilterKey } from "./types";

const ZIP_CODE_LENGTH = 8;

const DECIMAL_FILTER_PROPERTIES = new Set<CoverageAreaFilterKey>([
  "freightChargeOrder",
  "freightCostCarrier",
  "startWeight",
  "endWeight",
]);

const INTEGER_FILTER_PROPERTIES = new Set<CoverageAreaFilterKey>(["regionalDeliveryTime"]);

const normalizeZipCodeDigits = (value: unknown): string =>
  String(value ?? "").replace(/\D/g, "");

const normalizeNumericFilterValue = (
  property: CoverageAreaFilterKey,
  value: FilterValue
): FilterValue | null => {
  if (DECIMAL_FILTER_PROPERTIES.has(property)) {
    const parsed = typeof value === "number" ? value : Number(String(value));
    return Number.isNaN(parsed) ? null : parsed;
  }

  if (INTEGER_FILTER_PROPERTIES.has(property)) {
    const parsed = typeof value === "number" ? value : Number.parseInt(String(value), 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return value;
};

export const normalizeCoverageAreaFilters = (
  filters: CoverageAreaFilter[]
): IFilter<ICoverageArea>[] => {
  const result: IFilter<ICoverageArea>[] = [];

  for (const filter of filters) {
    if (filter.property === "servedZipCode") {
      const cep = normalizeZipCodeDigits(filter.value);
      if (cep.length !== ZIP_CODE_LENGTH) continue;

      result.push({
        property: "servedZipCode",
        filterType: FilterType.Equals,
        value: cep,
      } as IFilter<ICoverageArea>);
      continue;
    }

    const normalizedValue = normalizeNumericFilterValue(filter.property, filter.value);
    if (normalizedValue === null) continue;

    result.push({
      ...filter,
      value: normalizedValue,
    } as IFilter<ICoverageArea>);
  }

  return sanitizeFilters(normalizeSingleFilterFormData(result, "areaDescription"));
};
